/*eslint import/no-cycle: [2, { maxDepth: 1 }]*/
import React from 'react';
import { usePageTitle } from '../config';
import { styled } from '@mui/material/styles';
import '../../node_modules/@okta/okta-signin-widget/dist/css/okta-sign-in.min.css';
import { useLocalizationContext, useTranslate } from '@im/react-localization';
import { IngramLogoWithRX4CLoginHeader } from '@im/ui-lib/icons/IngramLogoWithRX4CLoginHeader';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { CulturePicker } from '@im/ui-lib/CulturePicker/CulturePicker';
import { LoginWidget } from './okta-widget-customizations/LoginWidget';
import { PageLoadTimeProvider } from '../provider/dd-custom-actions';

export const LoginView = () => {
  const translateStr = useTranslate();
  const copyrightContent = translateStr('OktaWidget.Copyright');
  const whatYearIsItNow = new Date().getFullYear().toString();
  const { updatePageTitle } = usePageTitle();
  const l10nContext = useLocalizationContext();
  const { locale } = l10nContext;
  const [, countryCode] = locale.split('-');

  const contactUsURLs = {
    BE: `/cep/app/cms/en-be/general/contact-us`,
    NL: `/cep/app/cms/nl-nl/general/contact-us`,
  };

  const isCountryCode = (code: string): code is keyof typeof contactUsURLs => {
    return code in contactUsURLs;
  };

  const openContactUsNewTab = (countryCode: string) => {
    if (isCountryCode(countryCode)) {
      (window as any).open(contactUsURLs[countryCode], '_blank');
    } else {
      console.error(
        'Invalid country code or missing URL for the provided country code.'
      );
    }
  };

  updatePageTitle(translateStr('Title.Login'));
  return (
    <PageLoadTimeProvider>
      <Root>
        <Stack
          direction={{ sm: 'row', xs: 'column' }}
          justifyContent={{ sm: 'space-between', xs: 'start' }}
          sx={{
            backgroundImage: `url('/cep/assets/img/login/login-header.webp')`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            height: 446,
          }}
        >
          <Box padding={{ sm: 4, xs: '2rem 0 0 2rem' }}>
            <IngramLogoWithRX4CLoginHeader />
          </Box>
          <Box padding={{ sm: 4, xs: '1rem 0 0 1rem' }}>
            <CulturePicker />
          </Box>
        </Stack>
        <Box
          sx={{
            backgroundImage: `url('/cep/assets/img/login/login-body.webp')`,
            backgroundPositionX: 'calc(50% + 230px)',
            backgroundPositionY: 88,
            backgroundRepeat: 'no-repeat',
            bgcolor: (theme) => theme.palette.grey[100],
            minHeight: 514,
          }}
        >
          <LoginWidget />
        </Box>
        <Box
          sx={{
            alignContent: 'center',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
          }}
        >
          <Typography
            align="center"
            color={(theme) => theme.palette.text.secondary}
            padding={3}
            variant="body2"
          >
            {copyrightContent.replace(/\{\{year\}\}/gi, whatYearIsItNow)}
          </Typography>
          {countryCode &&
            (countryCode.toUpperCase() === 'BE' ||
              countryCode.toUpperCase() === 'NL') && (
              <Button
                onClick={() => openContactUsNewTab(countryCode)}
                size="medium"
                sx={{
                  borderColor: '#C4C4C4 !important',
                  fontSize: '14px',
                  marginTop: 2,
                }}
                variant="outlined"
              >
                {translateStr('Login.Contact_us')}
              </Button>
            )}
        </Box>
        {window.isOneTrustEnabled && (
          <style>{`#onetrust-banner-sdk.otFlat {left:28px !important}`}</style>
        )}
      </Root>
    </PageLoadTimeProvider>
  );
};

const Root = styled('div')(({ theme }) => ({
  '#okta-sign-in .auth-header': {
    padding: '80px 90px 75px',
  },
  '#okta-sign-in .beacon-container': {
    bottom: 30,
  },
  '#okta-sign-in.auth-container': {
    '.mfa-google-auth,.mfa-google-auth-30': {
      backgroundImage:
        'url(/cep/assets/okta-signin-widget/img/icons/mfa/google-ms-authenticators_sm.png)',
      backgroundSize: 'contain',
    },
  },
  '#okta-sign-in.auth-container.main-container': {
    '.auth-content': {
      padding: 'unset',
    },
    '.button-primary': {
      background: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      borderRadius: 6,
      color: theme.palette.common.white,
      fontSize: 18,
      height: 56,
    },
    '.link': {
      color: theme.palette.text.primary,
      fontSize: 14,
    },
    '.o-form .infobox': {
      color: theme.palette.text.secondary,
    },
    '.o-form .input-fix': {
      border: 'unset',
      height: 52,
      input: {
        '&:-webkit-autofill': {
          WebkitBoxShadow: `0 0 0 25px ${theme.palette.common.white} inset`,
          WebkitTextFillColor: theme.palette.text.primary,
          borderRadius: 6,
        },
        '&::selection': {
          //backgroundColor: theme.palette.common.black,
        },
        border: `1px solid ${theme.palette.text.secondary}`,
        borderRadius: 6,
      },
    },
    '.o-form-explain': {
      color: 'unset',
      textAlign: 'left',
    },
    '.o-form-fieldset': {
      '&:first-of-type': {
        marginBottom: 40,
      },
    },
    '.o-form-head': {
      color: theme.palette.text.primary,
      fontSize: 16,
      fontWeight: 400,
      lineHeight: '18.75px',
      textAlign: 'left',
    },
    '.okta-form-input-field': {
      backgroundColor: 'unset',
      boxShadow: 'unset',
    },
    '.okta-form-label': {
      backgroundColor: theme.palette.grey[100],
      fontWeight: 400,
      left: 16,
      padding: '0 4px',
      position: 'relative',
      top: 10,
      width: 'auto',
      zIndex: 1,
    },
    '.okta-sign-in-header.auth-header': {
      '.beacon-container.beacon-small': {
        display: 'none',
      },
      borderBottom: 'unset',
    },
    '@media screen and (min-width: 950px)': {
      left: -220,
    },
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    boxShadow: 'none',
    color: theme.palette.text.primary,
    fontFamily: 'Roboto',
    input: {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.text.primary,
      fontFamily: 'unset',
    },
    margin: 'auto',
    overflow: 'unset',
    width: 412,
  },
  '#okta-sign-in.no-beacon .auth-header': {
    paddingBottom: 'unset',
  },
}));
